import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

/** Cierre del velo sobre la vista saliente (pantalla -> color liso). */
const COVER_MS = 220;
/** Disolución del velo sobre la vista entrante. */
const REVEAL_MS = 700;
/** Retirada forzada si una navegación se queda colgada con el velo puesto. */
const FAILSAFE_MS = 4000;
const LOGIN_PATH = '/login';

function pathOf(url: string): string {
  return url.split('?')[0].split('#')[0];
}

function isLoginUrl(url: string): boolean {
  return pathOf(url) === LOGIN_PATH;
}

/**
 * Transición de escena entre el acceso y el panel.
 *
 * El acceso es siempre oscuro y el panel respeta la elección del usuario
 * (claro por defecto): al cruzar esa frontera el cambio de piel es un salto
 * seco de fondo. Aquí se cubre la pantalla con el color de la vista saliente
 * (`.ts-scene-veil`, ver styles.css) y, cuando la navegación ya ha montado la
 * vista nueva, se disuelve revelándola: el usuario nunca ve los dos temas
 * conviviendo ni el fondo cambiar de golpe.
 *
 * Reparto:
 *  - `sceneGateGuard` llama a `curtainIn` antes de activar la ruta: el velo
 *    se cierra con la vista vieja todavía montada.
 *  - `App` llama a `curtainOut` al terminar la navegación (NavigationEnd o
 *    cualquiera de sus variantes de fallo).
 *  - `curtainIn` también retira la clase de entrada si la hubiera, para que
 *    la animación de la vista nueva se reproduzca de nuevo en el siguiente
 *    cruce.
 *
 * Con `prefers-reduced-motion` no hay velo: solo se cambia el tema justo
 * antes de montar la ruta nueva.
 */
@Injectable({ providedIn: 'root' })
export class SceneTransition {
  private veil: HTMLElement | null = null;
  private coverTimer: ReturnType<typeof setTimeout> | null = null;
  private revealTimer: ReturnType<typeof setTimeout> | null = null;
  private failsafeTimer: ReturnType<typeof setTimeout> | null = null;
  private settled = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  /** true si la navegación cruza la frontera acceso <-> panel. */
  crosses(from: string, to: string): boolean {
    return isLoginUrl(from) !== isLoginUrl(to);
  }

  /** Lo mismo que `crosses`, teniendo en cuenta si esta navegación va a
   *  llevar velo: `App` lo usa para no anticipar el cambio de piel. */
  willCurtain(from: string, to: string): boolean {
    return this.settled && this.crosses(from, to) && !this.reducedMotion();
  }

  /** La primera navegación (carga del documento) no lleva velo. */
  markSettled(): void {
    this.settled = true;
  }

  /** Cierra el velo sobre la vista saliente y espera a que cubra. */
  async curtainIn(from: string, to: string): Promise<void> {
    if (!this.willCurtain(from, to)) return;
    // Una revelación a medias se revierte: el velo vuelve a cubrir.
    if (this.revealTimer) { clearTimeout(this.revealTimer); this.revealTimer = null; }
    const veil = this.veil ?? this.mount();
    veil.classList.remove('is-revealing');
    veil.classList.add('is-covering');
    this.document.documentElement.classList.remove('ts-scene-reveal');
    if (this.failsafeTimer) clearTimeout(this.failsafeTimer);
    this.failsafeTimer = setTimeout(() => this.clear(), FAILSAFE_MS);
    await new Promise<void>(resolve => { this.coverTimer = setTimeout(resolve, COVER_MS); });
  }

  /** Disuelve el velo sobre la vista entrante y la retira al terminar. */
  curtainOut(): void {
    const veil = this.veil;
    if (!veil || veil.classList.contains('is-revealing')) return;
    if (this.coverTimer) { clearTimeout(this.coverTimer); this.coverTimer = null; }
    if (this.failsafeTimer) { clearTimeout(this.failsafeTimer); this.failsafeTimer = null; }
    veil.classList.remove('is-covering');
    veil.classList.add('is-revealing');
    this.document.documentElement.classList.add('ts-scene-reveal');
    this.revealTimer = setTimeout(() => { if (this.veil === veil) this.clear(); }, REVEAL_MS + 120);
  }

  /** Estado intermedio (velo puesto, sin revelar) o retirada completa. */
  private mount(): HTMLElement {
    const veil = this.document.createElement('div');
    veil.className = 'ts-scene-veil';
    veil.setAttribute('aria-hidden', 'true');
    veil.style.setProperty('--ts-scene-veil-color', this.outgoingColor());
    this.document.body.appendChild(veil);
    this.veil = veil;
    this.document.documentElement.classList.add('ts-scene-enter');
    return veil;
  }

  /** El color de la vista que se va: el fondo real que se está mirando. */
  private outgoingColor(): string {
    const view = this.document.defaultView;
    const source = this.document.querySelector('main.login-page') ?? this.document.body;
    const own = view?.getComputedStyle(source).backgroundColor ?? '';
    if (own && own !== 'transparent' && own !== 'rgba(0, 0, 0, 0)') return own;
    return view?.getComputedStyle(this.document.body).backgroundColor || '#030a1b';
  }

  private clear(): void {
    if (this.coverTimer) { clearTimeout(this.coverTimer); this.coverTimer = null; }
    if (this.revealTimer) { clearTimeout(this.revealTimer); this.revealTimer = null; }
    if (this.failsafeTimer) { clearTimeout(this.failsafeTimer); this.failsafeTimer = null; }
    this.veil?.remove();
    this.veil = null;
    const root = this.document.documentElement;
    root.classList.remove('ts-scene-enter', 'ts-scene-reveal');
  }

  private reducedMotion(): boolean {
    return !!this.document.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }
}
